import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { useAuth } from '@/auth/useAuth';
import { NarrowPage } from '@/components/NarrowPage';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import {
  getMyPurchases,
  getMyReservations,
  type BuyerPurchase,
  type BuyerReservation,
} from '@/features/account/api';
import { purchasePath } from '@/features/account/purchases';
import { describeReservedDevice } from '@/features/account/reservations';
import { formatPhone } from '@/features/customers/customerForm';
import { formatHistoryTimestamp } from '@/features/operations/labels';
import { usePriceDisplay } from '@/features/operations/PriceDisplayProvider';
import { StatusChip } from '@/components/StatusChip';

type ReservationsState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; reservations: BuyerReservation[] };

/// The date that matters for each state: when it ends, when it ended, or when it was made.
function reservationDateLabel(reservation: BuyerReservation): string {
  if (reservation.status === 'ACTIVE') {
    return `Vence el ${formatHistoryTimestamp(reservation.expiresAt)}`;
  }
  if (reservation.status === 'EXPIRED') {
    return `Venció el ${formatHistoryTimestamp(reservation.expiresAt)}`;
  }
  return `Reservada el ${formatHistoryTimestamp(reservation.createdAt)}`;
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" component="p">
        {label}
      </Typography>
      <Typography variant="body1">{value}</Typography>
    </Box>
  );
}

type PurchasesState =
  { status: 'loading' } | { status: 'error' } | { status: 'ready'; purchases: BuyerPurchase[] };

/// The buyer's online purchases (VEN-7), each linking to its page, which reads its status.
function MyPurchases() {
  const { token } = useAuth();
  const { formatNullablePrice } = usePriceDisplay();
  const [state, setState] = useState<PurchasesState>({ status: 'loading' });

  const load = useCallback(() => {
    if (!token) return;
    setState({ status: 'loading' });
    getMyPurchases(token)
      .then((purchases) => setState({ status: 'ready', purchases }))
      .catch(() => setState({ status: 'error' }));
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <SectionCard title="Mis compras">
      {state.status === 'loading' ? (
        <Stack direction="row" spacing={2} alignItems="center" role="status">
          <CircularProgress size={24} color="secondary" />
          <Typography>Cargando tus compras...</Typography>
        </Stack>
      ) : null}
      {state.status === 'error' ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={load}>
              Reintentar
            </Button>
          }
        >
          No pudimos cargar tus compras.
        </Alert>
      ) : null}
      {state.status === 'ready' && state.purchases.length === 0 ? (
        <Alert severity="info">
          Todavía no compraste ningún equipo. En el{' '}
          <Link component={RouterLink} to="/catalog" underline="always">
            catálogo
          </Link>{' '}
          podés comprar uno pagando con Mercado Pago.
        </Alert>
      ) : null}
      {state.status === 'ready' && state.purchases.length > 0 ? (
        <Stack spacing={1.5} component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
          {state.purchases.map((purchase) => (
            <Card component="li" key={purchase.id} variant="outlined">
              <CardActionArea component={RouterLink} to={purchasePath(purchase.id)}>
                <CardContent>
                  <Stack spacing={0.75}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography variant="subtitle1" component="h3">
                        {describeReservedDevice(purchase.device)}
                      </Typography>
                      <StatusChip domain="purchase" status={purchase.status} />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      Compra #{purchase.reference} · {formatNullablePrice(purchase.priceMinor)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {purchase.soldAt
                        ? `Pagada el ${formatHistoryTimestamp(purchase.soldAt)}`
                        : `Iniciada el ${formatHistoryTimestamp(purchase.createdAt)}`}
                    </Typography>
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Stack>
      ) : null}
    </SectionCard>
  );
}

/**
 * "Mi cuenta" of a buyer: their details (VEN-3), their purchases (VEN-7) and their reservations
 * (VEN-3). The buyer's quotations are not part of these issues.
 */
export function CustomerAccountPage() {
  const { user, token } = useAuth();
  const { formatNullablePrice } = usePriceDisplay();
  const [state, setState] = useState<ReservationsState>({ status: 'loading' });

  const load = useCallback(() => {
    if (!token) return;
    setState({ status: 'loading' });
    getMyReservations(token)
      .then((reservations) => setState({ status: 'ready', reservations }))
      .catch(() => setState({ status: 'error' }));
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  if (!user) return null;

  return (
    <NarrowPage maxWidth={720}>
      <PageHeader
        eyebrow="TU CUENTA DE PHONETEC"
        title="Mi cuenta"
        description="Tus datos, tus compras y tus reservas."
      />

      <SectionCard title="Tus datos">
        <Stack spacing={2}>
          <Detail label="Nombre" value={user.fullName} />
          <Detail label="Email" value={user.email} />
          <Detail label="Teléfono" value={formatPhone(user.phone)} />
          {user.emailVerified ? (
            <Chip color="success" label="Email verificado" sx={{ alignSelf: 'flex-start' }} />
          ) : (
            <Alert severity="warning">
              Tu email no está verificado: hasta verificarlo no podés reservar ni comprar.{' '}
              <Link
                component={RouterLink}
                to="/account/check-email"
                state={{ email: user.email }}
                underline="always"
              >
                Pedir el enlace
              </Link>
            </Alert>
          )}
        </Stack>
      </SectionCard>

      <MyPurchases />

      <SectionCard title="Mis reservas">
        {state.status === 'loading' ? (
          <Stack direction="row" spacing={2} alignItems="center" role="status">
            <CircularProgress size={24} color="secondary" />
            <Typography>Cargando tus reservas...</Typography>
          </Stack>
        ) : null}
        {state.status === 'error' ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={load}>
                Reintentar
              </Button>
            }
          >
            No pudimos cargar tus reservas.
          </Alert>
        ) : null}
        {state.status === 'ready' && state.reservations.length === 0 ? (
          <Alert severity="info">
            Todavía no reservaste ningún equipo. Elegí uno en el{' '}
            <Link component={RouterLink} to="/catalog" underline="always">
              catálogo
            </Link>{' '}
            y reservalo sin costo por 3 días.
          </Alert>
        ) : null}
        {state.status === 'ready' && state.reservations.length > 0 ? (
          <Stack spacing={1.5} component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
            {state.reservations.map((reservation) => (
              <Card component="li" key={reservation.id} variant="outlined">
                <CardActionArea
                  component={RouterLink}
                  to={`/account/reservations/${reservation.id}`}
                >
                  <CardContent>
                    <Stack spacing={0.75}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography variant="subtitle1" component="h3">
                          {describeReservedDevice(reservation.device)}
                        </Typography>
                        <StatusChip domain="reservation" status={reservation.status} />
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        Reserva #{reservation.reference} ·{' '}
                        {formatNullablePrice(reservation.priceMinor)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {reservationDateLabel(reservation)}
                      </Typography>
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Stack>
        ) : null}
      </SectionCard>
    </NarrowPage>
  );
}
